import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Loader2, MessageSquare } from 'lucide-react';
import { PhoneInput } from '@/components/ui/phone-input';
import { API } from '@/utils/API';

interface SMSSetupProps {
  onBack: () => void;
  phoneNumberMasked?: string;
}

type Step = 'phone' | 'verify';

const SMSSetup: React.FC<SMSSetupProps> = ({ onBack, phoneNumberMasked }) => {
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isSendingCode, setIsSendingCode] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [step, setStep] = useState<Step>('phone');
  const [isChangingPhone, setIsChangingPhone] = useState<boolean>(false);

  const validatePhoneNumber = useCallback((phone: string): boolean => {
    return phone.length >= 10 && /^\d+$/.test(phone.replace(/\D/g, ''));
  }, []);

  const handleSendCode = useCallback(async () => {
    // Validate phone number if user is changing phone or no masked number is provided
    if (isChangingPhone || !phoneNumberMasked) {
      if (!validatePhoneNumber(phoneNumber)) {
        setError('Please enter a valid phone number with at least 10 digits');
        return;
      }
    }

    setIsSendingCode(true);
    setError('');

    try {
      const response = await API.authenticated.fetch('/mfa/sms', {
        method: 'POST',
        body: JSON.stringify({
          phone_number: phoneNumber,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 'Failed to send verification code'
        );
      }

      setStep('verify');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to send verification code'
      );
    } finally {
      setIsSendingCode(false);
    }
  }, [phoneNumber, isChangingPhone, phoneNumberMasked, validatePhoneNumber]);

  const handleVerifyCode = useCallback(async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await API.authenticated.fetch('/mfa/sms', {
        method: 'PUT',
        body: JSON.stringify({
          sms_code: verificationCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 'Failed to verify verification code'
        );
      }

      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to verify verification code'
      );
    } finally {
      setIsVerifying(false);
    }
  }, [verificationCode]);

  const handleResendCode = useCallback(async () => {
    setVerificationCode('');
    setError('');
    await handleSendCode();
  }, [handleSendCode]);

  const handlePhoneNumberChange = useCallback((value: string) => {
    setPhoneNumber(value);
    setError('');
  }, []);

  const handleVerificationCodeChange = useCallback((value: string) => {
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(numericValue);
    setError('');
  }, []);

  const handleBackToPhone = useCallback(() => {
    setStep('phone');
    setVerificationCode('');
    setError('');
    setIsChangingPhone(false);
  }, []);

  const handleChangePhone = useCallback(() => {
    setIsChangingPhone(true);
    setPhoneNumber('');
    setError('');
  }, []);

  if (success) {
    return (
      <div className="text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          SMS Setup Complete!
        </h2>
        <p className="text-gray-600 mb-6">
          Your phone number has been successfully verified. You can now use SMS
          for multi-factor authentication.
        </p>
        <Button onClick={() => window.location.reload()} className="w-full">
          Continue
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Set up SMS Verification
        </h2>
        <p className="text-gray-600">
          Add your phone number to enable SMS-based two-factor authentication.
        </p>
      </div>

      {step === 'phone' ? (
        <>
          {/* Phone Number Input or Masked Display */}
          <div className="space-y-4">
            {phoneNumberMasked && !isChangingPhone ? (
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Send Verification Code
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Send verification to number ending in{' '}
                  <span className="font-medium">{phoneNumberMasked}</span>
                </p>
                <Button
                  variant="outline"
                  onClick={handleChangePhone}
                  className="text-sm"
                >
                  Change Phone Number
                </Button>
              </div>
            ) : (
              <div>
                <Label
                  htmlFor="phone-number"
                  className="text-sm font-medium text-gray-700"
                >
                  Phone Number
                </Label>
                <div className="mt-1">
                  <PhoneInput
                    id="phone-number"
                    value={phoneNumber}
                    onChange={e => handlePhoneNumberChange(e.target.value)}
                    placeholder="Enter your phone number"
                    defaultCountry="US"
                    className="text-lg"
                    aria-describedby="phone-number-help"
                  />
                </div>
                <p
                  id="phone-number-help"
                  className="text-xs text-gray-500 mt-1"
                >
                  Enter your phone number with country code
                </p>
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex space-x-3">
              <Button variant="outline" onClick={onBack} className="flex-1">
                Back
              </Button>
              <Button
                onClick={handleSendCode}
                disabled={
                  phoneNumberMasked && !isChangingPhone
                    ? isSendingCode
                    : !phoneNumber || phoneNumber.length < 10 || isSendingCode
                }
                className="flex-1"
              >
                {isSendingCode ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  'Send Code'
                )}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Verification Code Input */}
          <div className="space-y-4">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 text-blue-500 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Enter Verification Code
              </h3>
              <p className="text-sm text-gray-600">
                {phoneNumberMasked &&
                phoneNumberMasked.trim() !== '' &&
                !isChangingPhone ? (
                  <>
                    We sent a 6-digit code to number ending in{' '}
                    <span className="font-medium">{phoneNumberMasked}</span>
                  </>
                ) : (
                  <>
                    We sent a 6-digit code to{' '}
                    <span className="font-medium">{phoneNumber}</span>
                  </>
                )}
              </p>
            </div>

            <div>
              <Label
                htmlFor="verification-code"
                className="text-sm font-medium text-gray-700"
              >
                Verification Code
              </Label>
              <Input
                id="verification-code"
                type="text"
                value={verificationCode}
                onChange={e => handleVerificationCodeChange(e.target.value)}
                placeholder="123456"
                className="mt-1 text-center text-lg tracking-widest"
                maxLength={6}
                aria-describedby="verification-code-help"
                autoComplete="one-time-code"
              />
              <p
                id="verification-code-help"
                className="text-xs text-gray-500 mt-1"
              >
                Enter the 6-digit code sent to your phone
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isSendingCode}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                aria-label="Resend verification code"
              >
                {isSendingCode ? 'Sending...' : "Didn't receive a code? Resend"}
              </button>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleBackToPhone}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleVerifyCode}
                disabled={verificationCode.length !== 6 || isVerifying}
                className="flex-1"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Complete'
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SMSSetup;
